import { Icon } from '@natera/material/lib/icon'
import { useEffect, useMemo, useState } from 'react'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault } from '~/shared/lib/datetime'
import { IconButton, InfoTip, LocalIcon, RadioButton, Text } from '~/shared/ui'
import { NO_DATA_PLACEHOLDER } from '../../config'
import type { PatientMatch } from '../../model'
import {
  CardDivider,
  ContradictionChip,
  FieldRow,
  type FieldStatus,
} from '../pa-matches/pa-card-shared'
import { ClaimMatchCard } from './claim-match-card'

const CARD_MIN_WIDTH = 240

export function ClaimMatchesSection({
  patientData,
  containerWidth,
  selectedMatchIndex,
  selectedIndex,
  onSelect,
  onAddNewMatch,
  newlyAddedMatch,
  onRemoveNewMatch,
  documentFields,
  onClearSelection,
  showContradictions = false,
  hoveredField = null,
  currentPage: externalPage = 0,
  isMatchingV2 = false,
}: {
  patientData: apiClient.PatientRecordResponse[]
  containerWidth: number
  selectedMatchIndex?: number | null
  selectedIndex?: number
  onSelect?: (index: number) => void
  onAddNewMatch?: () => void
  newlyAddedMatch?: PatientMatch | null
  onRemoveNewMatch?: () => void
  documentFields?: Record<string, string | undefined>
  onClearSelection?: () => void
  showContradictions?: boolean
  hoveredField?: string | null
  currentPage?: number
  isMatchingV2?: boolean
}) {
  const sortedPatients = useMemo(() => {
    const raw = patientData ?? []
    const indexed = raw.map((record, originalIndex) => ({ record, originalIndex }))
    if (selectedMatchIndex == null) return indexed
    return [...indexed].sort((a, b) => {
      if (a.originalIndex === selectedMatchIndex) return -1
      if (b.originalIndex === selectedMatchIndex) return 1
      return 0
    })
  }, [patientData, selectedMatchIndex])

  const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set())
  const [fadingOutIndex, setFadingOutIndex] = useState<number | null>(null)
  const visiblePatients = sortedPatients.filter((p) => !hiddenIndexes.has(p.originalIndex))
  const totalCount = sortedPatients.length

  const MAX_CARDS = 3
  const CARD_GAP = 12
  const ADD_NEW_WIDTH = 132
  const PADDING = 64
  const hasAddButton = !!onAddNewMatch
  const addButtonSpace = hasAddButton ? ADD_NEW_WIDTH + CARD_GAP : 0
  const hasNewlyAdded = !!newlyAddedMatch
  const newlyAddedSpace = hasNewlyAdded ? CARD_MIN_WIDTH + CARD_GAP : 0
  const availableForCards = (containerWidth || 1280) - PADDING - addButtonSpace - newlyAddedSpace
  const cardsPerPage = Math.min(
    MAX_CARDS,
    Math.max(1, Math.floor((availableForCards + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))),
  )
  const totalSlots = cardsPerPage + (hasNewlyAdded ? 1 : 0)
  const cardWidth = Math.floor(
    ((containerWidth || 1280) - PADDING - addButtonSpace - (totalSlots - 1) * CARD_GAP) /
      totalSlots,
  )

  const totalPages = Math.ceil(visiblePatients.length / cardsPerPage)
  const page = Math.min(externalPage, Math.max(0, totalPages - 1))
  const paginatedPatients = visiblePatients.slice(page * cardsPerPage, (page + 1) * cardsPerPage)

  if (totalCount === 0 && !newlyAddedMatch) return null

  return (
    <div className="pb-4">
      <div
        className="mt-3 px-8 pb-2"
        style={{ width: containerWidth || '100%' }}
      >
        <div className="flex items-stretch gap-3">
          {newlyAddedMatch && (
            <div
              className="shrink-0"
              style={{ width: `${cardWidth}px` }}
            >
              <NewlyAddedClaimMatchCard
                match={newlyAddedMatch}
                isSelected={selectedIndex === newlyAddedMatch.matchIndex}
                onSelect={() => onSelect?.(newlyAddedMatch.matchIndex)}
                onRemove={onRemoveNewMatch}
                documentFields={documentFields}
                showContradictions={showContradictions}
                hoveredField={hoveredField}
              />
            </div>
          )}
          {paginatedPatients.map(({ record, originalIndex }) => {
            const isFadingOut = fadingOutIndex === originalIndex
            return (
              <div
                key={originalIndex}
                className={cn(
                  'shrink-0 transition-opacity duration-300',
                  isFadingOut && 'pointer-events-none opacity-0',
                )}
                style={{ width: `${cardWidth}px` }}
              >
                <ClaimMatchCard
                  record={record}
                  isSystemSelected={false}
                  isSelectable
                  isSelected={selectedIndex === originalIndex}
                  onSelect={() => onSelect?.(originalIndex)}
                  canHide={visiblePatients.length > 1}
                  showContradictions={showContradictions}
                  hoveredField={hoveredField}
                  documentFields={documentFields}
                  isUserCreated={
                    isMatchingV2 &&
                    !record.confidence_scores?.confidence_score &&
                    !record.matched_on?.length &&
                    !record.contradicted_on?.length
                  }
                  isMatchingV2={isMatchingV2}
                  connectUrl={record.connect_patient_service_url || undefined}
                  onHide={() => {
                    setFadingOutIndex(originalIndex)
                    setTimeout(() => {
                      if (selectedIndex === originalIndex) {
                        onClearSelection?.()
                      }
                      setHiddenIndexes((prev) => new Set([...prev, originalIndex]))
                      setFadingOutIndex(null)
                    }, 300)
                  }}
                />
              </div>
            )
          })}
          {onAddNewMatch && (
            <button
              className={cn(
                'border-divider bg-surface-lowest ml-auto flex shrink-0 cursor-pointer flex-col items-center justify-center rounded-lg border',
                isMatchingV2 && 'px-4',
              )}
              type="button"
              style={{ width: `${ADD_NEW_WIDTH}px`, minWidth: `${ADD_NEW_WIDTH}px` }}
              onClick={onAddNewMatch}
            >
              <Icon
                className="text-text-link text-[18px]"
                children="add"
              />
              <span
                className={cn(
                  'text-text-link mt-1 text-center leading-tight font-medium tracking-[1.25px] uppercase',
                  isMatchingV2 ? 'text-sm' : 'text-xs',
                )}
              >
                Add new match
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function compareField(
  matchValue: string | null | undefined,
  extractedValue: string | undefined,
  isDate = false,
): FieldStatus {
  if (!matchValue || !extractedValue) return 'indeterminate'
  const a = isDate ? formatDateDefault(matchValue) : matchValue.trim().toLowerCase()
  const b = isDate ? formatDateDefault(extractedValue) : extractedValue.trim().toLowerCase()
  return a === b ? 'match' : 'contradiction'
}

const ADDED_CARD_FIELDS = [
  {
    key: 'date_of_birth',
    label: 'Birth Date',
    matchField: 'date_of_birth',
    extractedField: 'member_dob',
    isDate: true,
  },
  {
    key: 'payor_name',
    label: 'Payor Name',
    matchField: 'payorname',
    extractedField: 'insurance_company',
  },
  { key: 'test_name', label: 'Test Name', matchField: 'test_name', extractedField: 'test_name' },
  {
    key: 'date_of_service',
    label: 'Date of Service',
    matchField: 'lims_date_of_service',
    extractedField: 'service_date_from',
    isDate: true,
  },
  {
    key: 'total_billed',
    label: 'Total Billed',
    matchField: 'billprice',
    extractedField: 'total_amount',
    isCurrency: true,
  },
  {
    key: 'appeal_date',
    label: 'Appeal Date',
    matchField: 'appeal_date',
    extractedField: 'appeal_date_received',
    isDate: true,
  },
  {
    key: 'member_id',
    label: 'Member ID',
    matchField: 'policy_number',
    extractedField: 'member_id',
  },
  {
    key: 'lims_case_id',
    label: 'LIMS Case ID',
    matchField: 'lims_casefile_id',
    extractedField: 'case_number',
  },
  {
    key: 'visit_id',
    label: 'Visit ID',
    matchField: 'visitfid',
    extractedField: 'patient_account_number',
  },
  {
    key: 'claim_id',
    label: 'Claim ID',
    matchField: 'claim_id',
    extractedField: 'natera_claim_id',
  },
  {
    key: 'cpt_codes',
    label: 'CPT Code(s)',
    matchField: 'allproccodes_claim',
    extractedField: 'cpt_code',
  },
] as const

function NewlyAddedClaimMatchCard({
  match,
  isSelected = false,
  onSelect,
  onRemove,
  documentFields,
  showContradictions = false,
  hoveredField = null,
}: {
  match: PatientMatch
  isSelected?: boolean
  onSelect?: () => void
  onRemove?: () => void
  documentFields?: Record<string, string | undefined>
  showContradictions?: boolean
  hoveredField?: string | null
}) {
  const [isContradictedExpanded, setIsContradictedExpanded] = useState(showContradictions)
  useEffect(() => {
    setIsContradictedExpanded(showContradictions)
  }, [showContradictions])

  const { fieldStatus, contradictedLabels } = useMemo(() => {
    if (!documentFields) {
      return {
        fieldStatus: (): FieldStatus => 'indeterminate',
        contradictedLabels: [] as string[],
      }
    }

    const statuses: Record<string, FieldStatus> = {}
    const contradicted: string[] = []

    for (const field of ADDED_CARD_FIELDS) {
      const { key, label, matchField, extractedField } = field
      const isDate = 'isDate' in field && field.isDate
      const matchValue = (match as unknown as Record<string, unknown>)[matchField] as
        | string
        | null
        | undefined
      const extractedValue = documentFields[extractedField]
      const result = compareField(matchValue, extractedValue, isDate)
      statuses[key] = result
      if (result === 'contradiction') contradicted.push(label)
    }

    return {
      fieldStatus: (key: string): FieldStatus => statuses[key] ?? 'indeterminate',
      contradictedLabels: contradicted,
    }
  }, [match, documentFields])

  return (
    <div
      className={cn(
        'bg-surface-lowest flex h-full flex-col overflow-hidden rounded-lg border-2 whitespace-nowrap',
        isSelected ? 'border-primary-main' : 'border-divider',
      )}
    >
      <div className="border-divider min-h-[52px] border-b">
        <button
          className="bg-surface-lowest relative flex h-full w-full cursor-pointer items-center justify-between border-none px-4 py-3"
          type="button"
          onClick={onSelect}
        >
          <RadioButton
            label="Added by user"
            withinButton
            checked={isSelected}
            onChange={() => onSelect?.()}
          />
        </button>
      </div>

      <div className="px-4 pt-2">
        <Text appearance="OVERLINE">Natera Patient Data</Text>
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <Text
          appearance="SUBTITLE"
          placeholder={NO_DATA_PLACEHOLDER}
          truncated
          withTooltip
          copyable
        >
          {match.patient_first_name} {match.patient_last_name}
        </Text>
        <FieldRow
          label="Birth Date"
          value={match.date_of_birth ? formatDateDefault(match.date_of_birth) : null}
          status={fieldStatus('date_of_birth')}
          hoveredField={hoveredField}
          hoveredFieldKey="birth_date"
          extractedValue={
            documentFields?.member_dob ? formatDateDefault(documentFields.member_dob) : undefined
          }
        />
        <FieldRow
          label="Payor Name"
          value={match.payorname}
          status={fieldStatus('payor_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="insurance_company"
          extractedValue={documentFields?.insurance_company}
        />
        <FieldRow
          label="Test Name"
          value={match.test_name}
          status={fieldStatus('test_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="test_name"
          extractedValue={documentFields?.test_name}
        />
        <FieldRow
          label="Date of Service"
          value={match.lims_date_of_service ? formatDateDefault(match.lims_date_of_service) : null}
          status={fieldStatus('date_of_service')}
          hoveredField={hoveredField}
          hoveredFieldKey="date_of_service"
          extractedValue={
            documentFields?.service_date_from
              ? formatDateDefault(documentFields.service_date_from)
              : undefined
          }
        />
        <FieldRow
          label="Total Billed"
          value={formatCurrency(match.billprice)}
          status={fieldStatus('total_billed')}
          hoveredField={hoveredField}
          hoveredFieldKey="billprice"
          extractedValue={documentFields?.total_amount}
        />
        <FieldRow
          label="Appeal Date"
          value={match.appeal_date ? formatDateDefault(match.appeal_date) : null}
          status={fieldStatus('appeal_date')}
          hoveredField={hoveredField}
          hoveredFieldKey="appeal_date"
          extractedValue={
            documentFields?.appeal_date_received
              ? formatDateDefault(documentFields.appeal_date_received)
              : undefined
          }
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <FieldRow
          label="Member ID"
          value={match.policy_number?.replace(/^\[|\]$/g, '')}
          status={fieldStatus('member_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="member_id"
          extractedValue={documentFields?.member_id}
        />
        <FieldRow
          label="LIMS Case ID"
          value={match.lims_casefile_id}
          status={fieldStatus('lims_case_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="case_number"
          extractedValue={documentFields?.case_number}
        />
        <FieldRow
          label="Visit ID"
          value={match.visitfid}
          status={fieldStatus('visit_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="patient_account_number"
          extractedValue={documentFields?.patient_account_number}
        />
        <FieldRow
          label="Payor Claim ID"
          value={match.claim_id}
          status={fieldStatus('claim_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="claim_number"
          extractedValue={documentFields?.natera_claim_id ?? documentFields?.claim_number}
        />
        <FieldRow
          label="CPT Code(s)"
          value={match.allproccodes_claim?.replace(/;/g, ', ')}
          status={fieldStatus('cpt_codes')}
          hoveredField={hoveredField}
          hoveredFieldKey="cpt_code"
          extractedValue={documentFields?.cpt_code}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col">
        <button
          className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-4 pt-4 text-left"
          type="button"
          onClick={() => setIsContradictedExpanded((v) => !v)}
        >
          <Text
            className="flex-1"
            appearance="OVERLINE"
          >
            Contradicted on{isContradictedExpanded ? '' : ` (${contradictedLabels.length})`}
          </Text>
          <Icon
            className="text-[20px]"
            children={isContradictedExpanded ? 'expand_less' : 'expand_more'}
          />
        </button>
        {isContradictedExpanded && (
          <div className="flex flex-wrap gap-1 px-4 py-3 whitespace-normal">
            {contradictedLabels.length > 0 ? (
              contradictedLabels.map((label) => (
                <ContradictionChip key={label}>{label}</ContradictionChip>
              ))
            ) : (
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                None
              </Text>
            )}
          </div>
        )}
      </div>

      <div className="grow" />
      <div className="border-divider flex items-center justify-between border-t p-4">
        <span className="text-text-link flex items-center gap-1">
          <Text appearance="BODY2">More</Text>
          <LocalIcon
            name="openInNew"
            size="18"
          />
        </span>
        {onRemove && (
          <button
            className="text-text-secondary flex cursor-pointer items-center gap-1 border-none bg-transparent p-0"
            type="button"
            onClick={onRemove}
          >
            <Icon
              className="text-[18px]"
              children="delete"
            />
            <Text appearance="BODY2">Delete</Text>
          </button>
        )}
      </div>
    </div>
  )
}

export function ClaimMatchesSectionHeader({
  totalCount,
  containerWidth,
  currentPage,
  onPageChange,
  hasAddButton = false,
  hasNewlyAdded = false,
}: {
  totalCount: number
  containerWidth: number
  currentPage: number
  onPageChange: (page: number) => void
  hasAddButton?: boolean
  hasNewlyAdded?: boolean
}) {
  const MAX_CARDS = 3
  const CARD_GAP = 12
  const ADD_NEW_WIDTH = 132
  const PADDING = 64
  const addButtonSpace = hasAddButton ? ADD_NEW_WIDTH + CARD_GAP : 0
  const newlyAddedSpace = hasNewlyAdded ? CARD_MIN_WIDTH + CARD_GAP : 0
  const availableForCards = (containerWidth || 1280) - PADDING - addButtonSpace - newlyAddedSpace
  const cardsPerPage = Math.min(
    MAX_CARDS,
    Math.max(1, Math.floor((availableForCards + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))),
  )
  const totalPages = Math.ceil(totalCount / cardsPerPage)
  const page = Math.min(currentPage, Math.max(0, totalPages - 1))

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Text appearance="H6">Select a Match</Text>
        <InfoTip
          iconSize="24"
          align="start"
        >
          <div className="-mx-1 -my-1 flex w-[296px] flex-col gap-2">
            <Text
              className="pr-6"
              appearance="SUBTITLE2"
            >
              Matching Legend
            </Text>
            <Text
              className="text-text-secondary"
              appearance="CAPTION"
            >
              Opti searched Natera's systems and pulled these potential matches. Select the relevant
              match.
            </Text>
            <div className="border-divider border-b" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Icon
                  className="text-chip-status-success-content shrink-0 text-[20px]"
                  children="check"
                />
                <Text appearance="CAPTION">Matches the document</Text>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  className="text-alert-error-message shrink-0 text-[20px]"
                  children="close"
                />
                <Text appearance="CAPTION">Contradicts the document</Text>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  className="text-text-secondary shrink-0 text-[20px]"
                  children="remove"
                />
                <Text appearance="CAPTION">No data</Text>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  className="text-text-secondary shrink-0 text-[20px]"
                  children="block"
                />
                <Text appearance="CAPTION">Not applicable</Text>
              </div>
            </div>
          </div>
        </InfoTip>
      </div>
      <div className="flex items-center gap-4">
        <Text appearance="BODY2">
          {page * cardsPerPage + 1} – {Math.min((page + 1) * cardsPerPage, totalCount)} of{' '}
          {totalCount}
        </Text>
        <IconButton
          size="medium"
          container="small"
          materialIcon="first_page"
          onClick={() => onPageChange(0)}
          disabled={page === 0}
          aria-label="First page"
        />
        <IconButton
          size="medium"
          container="small"
          materialIcon="chevron_left"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          aria-label="Previous page"
        />
        <IconButton
          size="medium"
          container="small"
          materialIcon="chevron_right"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          aria-label="Next page"
        />
        <IconButton
          size="medium"
          container="small"
          materialIcon="last_page"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={page >= totalPages - 1}
          aria-label="Last page"
        />
      </div>
    </div>
  )
}
